import React, { ReactNode } from "react";

import { Lock } from "./icons";
import styles from "./fundForm.module.scss";

interface IField {
  label: ReactNode;
  /** Shown under the control — what this field decides, not how to type it. */
  hint?: ReactNode;
  /** Why a field cannot be edited. Renders with a padlock beside the label. */
  lockedReason?: ReactNode;
  /** Marks the field with an asterisk. Required and optional are both stated. */
  required?: boolean;
  optional?: boolean;
  error?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * A labelled control.
 *
 * `lockedReason` exists because a disabled field with no explanation reads as
 * a bug. On a live fund most of the form is locked for good reasons, and the
 * reason is the useful part.
 */
export default function Field({
  label,
  hint,
  lockedReason,
  required,
  optional,
  error,
  className,
  children,
}: IField) {
  const isLocked = Boolean(lockedReason);

  return (
    <div
      className={`${styles.field} ${className ?? ""}`}
      // The label is what a person calls this field, so it is also what a test
      // asks for. antd's controls carry no id of their own to point at.
      data-field={typeof label === "string" ? label : undefined}
    >
      <label
        className={`${styles.label} ${isLocked ? styles.labelLocked : ""}`}
      >
        {label}
        {/*
          Both are said out loud. A form where only some fields are marked
          leaves the reader guessing what the unmarked ones are.
        */}
        {required && !isLocked ? (
          <span className={styles.requiredMark} aria-hidden="true">
            *
          </span>
        ) : null}
        {optional ? <span className={styles.optional}>(optional)</span> : null}
        {isLocked ? <Lock color="#b9bfc7" /> : null}
      </label>

      <div className={styles.control}>{children}</div>

      {error ? <span className={styles.error}>{error}</span> : null}
      {!error && lockedReason ? (
        <span className={styles.hintFaint}>{lockedReason}</span>
      ) : null}
      {!error && !lockedReason && hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}
